import React, { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Col, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { City, Country, State } from "country-state-city";

import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";

// Components
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import {
  autocompleteDropdownStyles,
  getDisabledInputStyles,
} from "Constants/Style";

import "react-toastify/dist/ReactToastify.css";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import { fetchAddressFromPincode } from "helpers/geonamesHelper";

interface WarehouseFormValues {
  warehouse_name: string;
  warehouse_code: string;
  warehouse_type: string;
  enable_FIFO: string;
  address: string;
  country: string;
  state: string;
  city: string;
  pincode: string;
  latitude: string;
  longitude: string;
  district: string;
}

const commonInputStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
};

const AddWarehouseForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Dropdown Data State
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  // Initial Values
  const initialValues: WarehouseFormValues = {
    warehouse_name: "",
    warehouse_code: "",
    warehouse_type: "Project Warehouse",
    enable_FIFO: "",
    address: "",
    country: "",
    state: "",
    city: "",
    district: "",
    pincode: "",
    latitude: "",
    longitude: "",
  };

  // Validation Schema
  const validationSchema = Yup.object().shape({
    warehouse_name: Yup.string().trim().required("Warehouse name is required"),
    warehouse_code: Yup.string()
      .trim()
      .required("Warehouse code is required")
      .matches(
        /^[A-Za-z0-9_-]+$/,
        "Warehouse code must contain only letters, numbers, hyphens or underscores",
      ),
    address: Yup.string().trim().required("Address is required"),
    country: Yup.string().required("Country is required"),
    state: Yup.string().when([], {
      is: () => states.length > 0,
      then: (schema) => schema.required("State is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    city: Yup.string().when([], {
      is: () => cities.length > 0,
      then: (schema) => schema.required("City is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    pincode: Yup.string()
      .trim()
      .required("Pincode is required")
      .matches(/^\d{6}$/, "Pincode must be 6 digits"),
    // latitude: Yup.string().trim().required("Latitude is required"),
    // longitude: Yup.string().trim().required("Longitude is required"),
  });

  const handlePincodeBlur = async () => {
    const pin = formik.values.pincode?.trim();
    if (!pin) return;

    const selectedCountry = countries.find(
      (c: any) => c.name === formik.values.country
    );
    const countryCode = selectedCountry?.isoCode;

    try {
      const data = await fetchAddressFromPincode(pin, countryCode);
      if (data) {
        const resolvedCountry = countries.find(
          (c: any) => c.isoCode === data.countryCode
        ) || Country.getAllCountries().find(
          (c: any) => c.isoCode === data.countryCode
        );

        if (resolvedCountry) {
          formik.setFieldValue("country", resolvedCountry.name);

          const countryStates = State.getStatesOfCountry(resolvedCountry.isoCode);
          setStates(countryStates);

          const resolvedState = countryStates.find(
            (s: any) =>
              s.name?.toLowerCase() === data.adminName1?.toLowerCase() ||
              s.isoCode?.toLowerCase() === data.adminCode1?.toLowerCase()
          );

          if (resolvedState) {
            formik.setFieldValue("state", resolvedState.name);

            const stateCities = City.getCitiesOfState(
              resolvedCountry.isoCode,
              resolvedState.isoCode
            );
            setCities(stateCities);
            formik.setFieldValue("city", data.placeName || "");
          } else {
            formik.setFieldValue("state", data.adminName1 || "");
            formik.setFieldValue("city", data.placeName || "");
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch address details for pincode", err);
    }
  };

  // Formik
  const formik = useFormik<WarehouseFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  // Load Initial Countries
  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
  }, []);

  // Fetch Data for Edit
  useEffect(() => {
    if (id) {
      loadWarehouseData(id);
    }
  }, [id]);

  async function loadWarehouseData(warehouseId: string) {
    setIsLoading(true);
    try {
      const res = await Axios.get(
        `${API_ENDPOINT.MASTERS.WAREHOUSE.DETAIL(warehouseId)}`,
      );
      const response = res?.data || {};

      if (Object.keys(response).length > 0) {
        const data = response.data || response;

        // Determine Locations for dropdowns
        const allCountries = Country.getAllCountries();

        // Find country with robust matching (Handle case sensitivity and trimming)
        let selectedCountry = allCountries.find(
          (c: any) => c.name === data.country,
        );
        if (!selectedCountry && data.country) {
          const cleanCountry = String(data.country).trim().toLowerCase();
          selectedCountry = allCountries.find(
            (c: any) => c.name.toLowerCase() === cleanCountry,
          );
        }

        const countryIsoCode = selectedCountry?.isoCode || "";

        const statesOfCountry = State.getStatesOfCountry(countryIsoCode);

        // Find state with robust matching
        let selectedState = statesOfCountry.find(
          (s: any) => s.name === data.state,
        );
        if (!selectedState && data.state) {
          const cleanState = String(data.state).trim().toLowerCase();
          selectedState = statesOfCountry.find(
            (s: any) => s.name.toLowerCase() === cleanState,
          );
        }

        const stateIsoCode = selectedState?.isoCode || "";

        const citiesOfState = City.getCitiesOfState(
          countryIsoCode,
          stateIsoCode,
        );

        // Find city with robust matching
        let selectedCity = citiesOfState.find((c: any) => c.name === data.city);
        if (!selectedCity && data.city) {
          const cleanCity = String(data.city).trim().toLowerCase();
          selectedCity = citiesOfState.find(
            (c: any) => c.name.toLowerCase() === cleanCity,
          );
        }

        // Update Location States
        setStates(statesOfCountry);
        setCities(citiesOfState);

        // Set Form Values
        formik.setValues({
          warehouse_name: data.warehouse_name || "",
          warehouse_code: data.warehouse_code || "",
          warehouse_type: data.warehouse_type || "Project Warehouse",
          enable_FIFO: data.enable_FIFO || "",
          address: data.address || "",
          country: selectedCountry ? selectedCountry.name : data.country || "",
          state: selectedState ? selectedState.name : data.state || "",
          city: selectedCity ? selectedCity.name : data.city || "",
          district: data.district || "",
          pincode: data.pincode || "",
          latitude: data.latitude || "",
          longitude: data.longitude || "",
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch warehouse details");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSubmit(values: WarehouseFormValues) {
    setIsSubmitting(true);
    try {
      const payload = {
        ...values,
        warehouse_name: values.warehouse_name.trim(),
        warehouse_code: values.warehouse_code.trim(),
        address: values.address.trim(),
        pincode: values.pincode.trim(),
        latitude: values.latitude.trim(),
        longitude: values.longitude.trim(),
      };

      let response;
      if (id) {
        response = await Axios.put(
          `${API_ENDPOINT.MASTERS.WAREHOUSE.UPDATE(id)}`,
          payload,
        );
      } else {
        response = await Axios.post(
          `${API_ENDPOINT.MASTERS.WAREHOUSE.CREATE}`,
          payload,
        );
      }

      if (response.status === 200 || response.status === 201) {
        if (!id) {
          clearTableState(TableKeys.MASTER.WAREHOUSE);
        }
        toast.success(`Warehouse ${id ? "updated" : "created"} successfully`);
        navigate("/master/warehouse-list");
      } else {
        toast.error(`Failed to ${id ? "update" : "create"} warehouse`);
      }
    } catch (error: any) {
      let errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to save warehouse";

      if (
        typeof errorMsg === "string" &&
        errorMsg.includes("user with this email already exists")
      ) {
        toast.error("user with this email already exists");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Warehouse" : "Add Warehouse"}
        titleLink="/master/warehouse-list"
        breadcrumbs={[
          { label: "Warehouse", to: "/master/warehouse-list" },
          { label: id ? "Edit Warehouse" : "Add Warehouse", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {id && isLoading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Row className="mb-4">
            <Col xs={12} md={4} className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Warehouse Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="warehouse_name"
                  value={formik.values.warehouse_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!(
                      formik.errors.warehouse_name &&
                      formik.touched.warehouse_name
                    )
                  }
                  placeholder="Enter Warehouse Name"
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.warehouse_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={4} className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Warehouse Code <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="warehouse_code"
                  value={formik.values.warehouse_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!(
                      formik.errors.warehouse_code &&
                      formik.touched.warehouse_code
                    )
                  }
                  placeholder="Enter Warehouse Code"
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.warehouse_code}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Address <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(formik.errors.address && formik.touched.address)
                    }
                    placeholder="Search Address"
                    style={commonInputStyle}
                    className="form-control-placeholder"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.address}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={4} xs={12} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Pincode <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="pincode"
                  placeholder="Enter Pincode"
                  value={formik.values.pincode}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    formik.setFieldValue("pincode", numericValue);
                  }}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handlePincodeBlur();
                  }}
                  isInvalid={
                    !!(formik.errors.pincode && formik.touched.pincode)
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.pincode}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4} xs={12} className="mb-3">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Country <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    countries.find(
                      (c: any) =>
                        c.name?.toLowerCase() ===
                        formik.values.country?.toLowerCase(),
                    ) || null
                  }
                  onChange={(event, newValue: any) => {
                    const selectedCountryName = newValue?.name || "";
                    formik.setFieldValue("country", selectedCountryName, true);
                    formik.setFieldValue("state", "", false);
                    formik.setFieldValue("city", "", false);

                    if (newValue) {
                      setIsLoadingStates(true);
                      const newStates = State.getStatesOfCountry(
                        newValue.isoCode,
                      );
                      setStates(newStates);
                      setCities([]);
                      setIsLoadingStates(false);
                    } else {
                      setStates([]);
                      setCities([]);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("country", true)}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt.name,
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="country"
                      placeholder="Select Country"
                      variant="outlined"
                      size="small"
                      error={
                        !!(formik.errors.country && formik.touched.country)
                      }
                      helperText={
                        formik.touched.country
                          ? (formik.errors.country as string)
                          : ""
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>

            <Col md={4} xs={12} className="mb-3">
              <Form.Group>
                {(() => {
                  const hasStates = states.length > 0;
                  return (
                    <>
                      <Form.Label className="fw-semibold">
                        {hasStates ? "State" : "State / Region"}{" "}
                        {hasStates && <span className="text-danger">*</span>}
                      </Form.Label>
                      {hasStates ? (
                        <Autocomplete
                          options={states}
                          getOptionLabel={(option) => option.name || ""}
                          loading={isLoadingStates}
                          value={
                            states.find(
                              (s: any) =>
                                s.name?.toLowerCase() ===
                                formik.values.state?.toLowerCase(),
                            ) || null
                          }
                          onChange={(event, newValue: any) => {
                            const selectedStateName = newValue?.name || "";
                            formik.setFieldValue(
                              "state",
                              selectedStateName,
                              true,
                            );
                            formik.setFieldValue("city", "", false);

                            const selectedCountry = countries.find(
                              (c: any) =>
                                c.name?.toLowerCase() ===
                                formik.values.country?.toLowerCase(),
                            );

                            if (selectedCountry && newValue) {
                              setIsLoadingCities(true);
                              const newCities = City.getCitiesOfState(
                                selectedCountry.isoCode,
                                newValue.isoCode,
                              );
                              setCities(newCities);
                              setIsLoadingCities(false);
                            } else {
                              setCities([]);
                            }
                          }}
                          onBlur={() => formik.setFieldTouched("state", true)}
                          disabled={!formik.values.country}
                          ListboxProps={autocompleteDropdownStyles.ListboxProps}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => opt.name,
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="state"
                              placeholder="Select State"
                              variant="outlined"
                              size="small"
                              error={
                                !!(formik.errors.state && formik.touched.state)
                              }
                              helperText={
                                formik.touched.state
                                  ? (formik.errors.state as string)
                                  : ""
                              }
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                ...getDisabledInputStyles(
                                  !formik.values.country,
                                ),
                              }}
                            />
                          )}
                        />
                      ) : (
                        <Form.Control
                          type="text"
                          name="state"
                          value={formik.values.state}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Enter State / Region"
                          style={commonInputStyle}
                          disabled={!formik.values.country}
                        />
                      )}
                    </>
                  );
                })()}
              </Form.Group>
            </Col>

            <Col md={4} xs={12} className="mb-3">
              <Form.Group>
                {(() => {
                  const hasStates = states.length > 0;
                  const hasCities = cities.length > 0;
                  const showCityDropdown =
                    hasStates && (!formik.values.state || hasCities);

                  return (
                    <>
                      <Form.Label className="fw-semibold">
                        City{" "}
                        {showCityDropdown && (
                          <span className="text-danger">*</span>
                        )}
                      </Form.Label>
                      {showCityDropdown ? (
                        <Autocomplete
                          options={cities}
                          getOptionLabel={(option) => option.name || ""}
                          loading={isLoadingCities}
                          value={
                            cities.find(
                              (c: any) =>
                                c.name?.toLowerCase() ===
                                formik.values.city?.toLowerCase(),
                            ) || null
                          }
                          onChange={(event, newValue: any) => {
                            formik.setFieldValue("city", newValue?.name || "");
                          }}
                          onBlur={() => formik.setFieldTouched("city", true)}
                          disabled={!formik.values.state}
                          ListboxProps={autocompleteDropdownStyles.ListboxProps}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => opt.name,
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="city"
                              placeholder="Select City"
                              variant="outlined"
                              size="small"
                              error={
                                !!(formik.errors.city && formik.touched.city)
                              }
                              helperText={
                                formik.touched.city
                                  ? (formik.errors.city as string)
                                  : ""
                              }
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                ...getDisabledInputStyles(!formik.values.state),
                              }}
                            />
                          )}
                        />
                      ) : (
                        <Form.Control
                          type="text"
                          name="city"
                          value={formik.values.city}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Enter City"
                          style={commonInputStyle}
                          disabled={!formik.values.country}
                        />
                      )}
                    </>
                  );
                })()}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col xs={12} md={4} className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Latitude 
                </Form.Label>
                <Form.Control
                  type="text"
                  name="latitude"
                  value={formik.values.latitude}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!(formik.errors.latitude && formik.touched.latitude)
                  }
                  placeholder="Enter Latitude"
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.latitude}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Longitude 
                </Form.Label>
                <Form.Control
                  type="text"
                  name="longitude"
                  value={formik.values.longitude}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!(formik.errors.longitude && formik.touched.longitude)
                  }
                  placeholder="Enter Longitude"
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.longitude}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              redirectTo="/master/warehouse-list"
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default AddWarehouseForm;
